import { useState, useCallback } from "react";
import AttendanceServices from "../services/AttendanceServices";
import { message } from "antd";
import useStore from "../store/store";

const useAttendance = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const selectedStore = useStore((state) => state.selectedStore);
  const storeId = selectedStore?._id;

  // Clock In
  const clockIn = useCallback(
    async (employeeId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await AttendanceServices.clockIn(employeeId, storeId);
        message.success(response.message || "Clocked in successfully");
        return response;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to clock in";
        setError(errorMessage);
        message.error(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [storeId]
  );

  // Clock Out
  const clockOut = useCallback(
    async (employeeId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await AttendanceServices.clockOut(employeeId, storeId);
        message.success(response.message || "Clocked out successfully");
        return response;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to clock out";
        setError(errorMessage);
        message.error(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [storeId]
  );

  // Get Today's Status
  const getTodayStatus = useCallback(
    async (employeeId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await AttendanceServices.getTodayStatus(
          employeeId,
          storeId
        );
        return response;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to get today's status";
        setError(errorMessage);
        console.error(errorMessage, err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [storeId]
  );

  // Get Employee Attendance
  const getEmployeeAttendance = useCallback(
    async (employeeId, params) => {
      try {
        setLoading(true);
        setError(null);
        const response = await AttendanceServices.getEmployeeAttendance(
          employeeId,
          storeId,
          params
        );
        return response;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to get attendance records";
        setError(errorMessage);
        console.error(errorMessage, err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [storeId]
  );

  // Get All Employees Attendance
  const getAllEmployeesAttendance = useCallback(async (params, storeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await AttendanceServices.getAllEmployeesAttendance({
        ...params,
        storeId,
      });
      return response;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to get attendance records";
      setError(errorMessage);
      console.error(errorMessage, err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create Manual Attendance
  const createManualAttendance = useCallback(
    async (attendanceData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await AttendanceServices.createManualAttendance(
          attendanceData,
          storeId
        );
        message.success(
          response.message || "Attendance record created successfully"
        );
        return response;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to create attendance record";
        setError(errorMessage);
        message.error(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [storeId]
  );

  // Update Attendance
  const updateAttendance = useCallback(
    async (attendanceId, updateData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await AttendanceServices.updateAttendance(
          attendanceId,
          updateData,
          storeId
        );
        message.success(
          response.message || "Attendance record updated successfully"
        );
        return response;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to update attendance record";
        setError(errorMessage);
        message.error(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [storeId]
  );

  // Delete Attendance
  const deleteAttendance = useCallback(
    async (attendanceId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await AttendanceServices.deleteAttendance(
          attendanceId,
          storeId
        );
        message.success(
          response.message || "Attendance record deleted successfully"
        );
        return response;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to delete attendance record";
        setError(errorMessage);
        message.error(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [storeId]
  );

  return {
    loading,
    error,
    clockIn,
    clockOut,
    getTodayStatus,
    getEmployeeAttendance,
    getAllEmployeesAttendance,
    createManualAttendance,
    updateAttendance,
    deleteAttendance,
  };
};

export default useAttendance;
